import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from "react";
import useToast from "./useToast";
import { useQuery } from "react-query";
import { Context } from "../../App";
import { useNavigate, useParams } from "react-router-dom";
import useDialog from "./useDialog";
import { Box, Center, List } from "@chakra-ui/react";
import { Button } from "@/components/ui/button";
import { RoutePath, SPACING } from "@/generic/config";
import Icon, { Icons } from "../Icons";
import Text from "../Text";
import { localStorageKeys } from "@/generic/constants";

export type ValidationMessage<M,> = {
    field: keyof M;
    description: string;
    severity: "error" | "warning";
}

export type BasicModel<M> = {
    typeProviderObject: M;
}

export type ModelDefinition<M,> = {
    getById: (id: number) => Promise<M>;
    saveFromModel: (model: { object: M, initialObject: M }) => Promise<M>;
    createFromModel: (model: M) => Promise<number>;
    validationMessages: (model: M) => ValidationMessage<M>[];
    routePath: RoutePath;
    typeProviderObject: M;
}

export const getBasicModelStateless = <M,>(initialObject: M): Model<M> => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    // const [object, setObject] = useState<M>(initialObject);
    let object: M = initialObject;
    const setObject: Dispatch<SetStateAction<M>> = (value) => {
        if (typeof value === "function") {
            object = (value as (prevState: M) => M)(object);
        } else {
            object = value;
        }
    };

    return {
        get object() {
            return object;
        },
        initialObject,
        setObject,
        isLoading: false,
        save: async () => { throw new Error("Basic model cannot be saved!") },
        isDirty: false,
        hasError: false,
        hasWarning: false,
        validationMessages: [],
        typeProviderObject: initialObject,
        isNew: true,
    };
};

export const GetBasicModelWState = <M,>(initialObject: M) => {
    const [object, setObject] = useState<M>(initialObject);
    return {
        get object() {
            return object;
        },
        initialObject,
        setObject,
        isLoading: false,
        save: async () => { throw new Error("Basic model cannot be saved!") },
        isDirty: false,
        hasError: false,
        hasWarning: false,
        validationMessages: [],
        typeProviderObject: initialObject,
        isNew: true,
    };
};

const convertDates = <M,>(data: M, typeProvider: M): M => {
    const result = { ...data };
    for (const key in typeProvider) {
        if (typeProvider[key] instanceof Date && typeof result[key] === 'string') {
            (result as any)[key] = new Date(result[key] as string);
        }
    }
    return result;
};

const useModel = <M,>(model: ModelDefinition<M>) => {
    const { translations } = useContext(Context);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const params = useParams<{ id: string }>();
    const isNew = window.location.pathname.split('/').pop() === "new";
    const objectId = parseInt(params.id ?? "-1");
    const objectQuery = useQuery(`${model.routePath}-${objectId}`, () => isNew ? Promise.resolve(model.typeProviderObject) : model.getById(objectId));

    const hasUsedPersistedContext = useRef(false);
    const getPersistedContext = useCallback((): Partial<M> | null => {
        if (!isNew) {
            return null;
        }

        const stored = localStorage.getItem(localStorageKeys.editorPersistedContext);
        if (!stored) {
            return null;
        }
        localStorage.removeItem(localStorageKeys.editorPersistedContext);
        try {
            return JSON.parse(stored) as Partial<M>;
        } catch {
            return null;
        }
    }, [isNew]);

    const [object, setObject] = useState<M>(model.typeProviderObject as M);
    const [initialObject, setInitialObject] = useState<M>({} as M);
    const [isDirty, setIsDirty] = useState(false);

    const hasError = model.validationMessages(object).some(message => message.severity === "error");
    const hasWarning = model.validationMessages(object).some(message => message.severity === "warning");
    const allMessages = model.validationMessages(object);
    const toast = useToast();
    const { openDialog } = useDialog();

    useEffect(() => {
        // TODO: this isNew is shit, fix later. Maybe can be removed since promise.resove in objectQuiery?
        if (isNew && !hasUsedPersistedContext.current) {
            const persistedContext = getPersistedContext();
            const obj = persistedContext ? { ...model.typeProviderObject, ...persistedContext } : model.typeProviderObject;
            setObject(obj);
            setInitialObject(obj);
            hasUsedPersistedContext.current = true;
            return;
        }

        objectQuery.refetch();
    }, [objectId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Set object when data is fetched
    useEffect(() => {
        if (isNew) {
            console.log("Object is new, not setting types");
            return;
        }
        const result = { ...objectQuery.data } as M;
        if (Object.keys(result as any).length === 0) {
            setObject(model.typeProviderObject);
            setInitialObject(model.typeProviderObject);
        } else {
            const converted = convertDates({ ...objectQuery.data } as M, model.typeProviderObject);
            setObject(converted);
            setInitialObject(converted);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectQuery.data, isNew, model.typeProviderObject, getPersistedContext]);

    // Set loading state
    useEffect(() => {
        setIsLoading(objectQuery.isLoading);
    }, [objectQuery.isLoading]);

    // Save and refetch
    const save = async () => {

        const doCreate = async () => {
            const newId = await model.createFromModel(object);
            if (isNew) {
                navigate(`${model.routePath}/${newId}`);
            }
        }

        const doSave = async () => {
            try {
                setIsLoading(true);
                await model.saveFromModel({ object, initialObject });
                toast(translations.save.saved.title, translations.save.saved.description, "success");
                objectQuery.refetch();
            } finally {
                setIsLoading(false);
            }
        }

        const saveOrCreate = async () => {
            if (isNew) {
                doCreate();
            } else {
                doSave();
            }
        }

        if (hasError) {
            const body = (
                <Box px={SPACING * 2}>
                    <Text variant="subtitle">{translations.save.errorsArePresent.description}</Text>
                    <Box mt={SPACING * 2} pl={SPACING}>
                        <List.Root>
                            {allMessages.map((message, index) => (
                                <List.Item display="flex" alignContent="center" alignItems="center" key={`${message.field.toString()}-${index}`} >
                                    <Icon marginRight={SPACING} icon={Icons.Warning} color={message.severity === "error" ? "red" : "orange"} key={`${message.field.toString()}-${index}`} />
                                    {message.description}
                                </List.Item>
                            ))}
                        </List.Root>
                    </Box>
                </Box>
            );
            openDialog({
                title: translations.save.errorsArePresent.title,
                body: body,
                size: "md",
                buttons: [
                    {
                        title: translations.save.errorsArePresent.buttons.cancel,
                        onClick: () => { }
                    },
                ]
            });
            console.log("Could not save, errors are present", allMessages, object);
            // toast(translations.save.errorsArePresent.title, translations.save.errorsArePresent.description, "error");
            return;
        }

        if (hasWarning) {
            const body = (
                <>
                    {translations.save.warningsArePresent.description}
                    <Box mt={SPACING}>
                        <List.Root>
                            {allMessages.map((message, index) => (
                                <List.Item key={`${message.field.toString()}-${index}`} display="flex" alignContent="center" alignItems="center" mb={SPACING}>
                                    <Icon icon={Icons.Warning} color={message.severity === "error" ? "red" : "orange"} mr={SPACING / 2} key={`${message.field.toString()}-${index}`} />
                                    {message.description}
                                </List.Item>
                            ))}
                        </List.Root>
                    </Box>
                </>
            )

            openDialog({
                title: translations.save.warningsArePresent.title,
                body: body,
                buttons: [
                    {
                        title: translations.save.warningsArePresent.buttons.cancel,
                        onClick: () => { }
                    },
                    {
                        title: translations.save.warningsArePresent.buttons.saveAnyway,
                        onClick: async () => {
                            await saveOrCreate();
                        }
                    },
                ]
            });
            return;
        }

        await saveOrCreate();
    };

    // Set dirty state
    useEffect(() => {
        setIsDirty(JSON.stringify(object) !== JSON.stringify(initialObject));
    }, [object, initialObject]);


    const toolbar = (
        <Box>
            <Box display={isDirty || isNew ? "block" : "none"} width={"100%"} zIndex={99} left={0} py={SPACING * 4} />
            <Box display={isDirty || isNew ? "block" : "none"} position={"fixed"} bottom={0} width={"100%"} zIndex={99} left={0} backgroundColor={"gray"} py={SPACING / 2}>
                <Center>
                    <Button px={SPACING * 2} onClick={save}>
                        {translations.save.save}
                    </Button>
                </Center>
            </Box>
        </Box>

    );

    return {
        model: {
            object,
            initialObject,
            setObject,
            isLoading: isLoading,
            save: save,
            isDirty: isDirty,
            hasError: hasError,
            hasWarning: hasWarning,
            validationMessages: model.validationMessages(object),
            typeProviderObject: model.typeProviderObject,
            isNew,
        },
        toolbar,
    };
};

export type Model<M> = ReturnType<typeof useModel<M>>["model"];


export default useModel;


export const getFieldValidity = <M,>(validationMessages: ValidationMessage<M>[], field: keyof M) => {
    const messages = validationMessages.filter(message => message.field === field);
    const severity = messages.some(message => message.severity === "error") ? "error" : "warning";
    return {
        isValid: messages.length === 0,
        messages: messages,
        severity: severity,
    }
}

export const validationMessage = <M,>(field: keyof M, description: string, severity: "error" | "warning") => {
    return {
        field: field,
        description: description,
        severity: severity,
    }
}